import * as THREE from 'three';

/**
 * Stub / plantilla: plano de video con transparencia (alpha WebM o chroma key).
 *
 * Samples opcionales: `npm run generate:video` → `public/assets/intro-*.{webm,mp4}`.
 * Sin URL válida o sin archivo, `attachTransparentVideo` no monta nada (no-op).
 *
 * Mattercraft: preferir VideoPlayer + chromaKey / sideBySide (ver docs/transparent-video.md).
 * UAR Three.js: este stub usa VideoTexture; chroma es un shader mínimo, no un pipeline de producción.
 */

export const VIDEO_MODES = /** @type {const} */ (['alpha', 'chroma']);

/**
 * @param {string} hex #rrggbb
 * @returns {THREE.Color}
 */
export function parseChromaKey(hex = '#00ff00') {
  const c = new THREE.Color('#00ff00');
  if (typeof hex === 'string' && /^#?[0-9a-fA-F]{6}$/.test(hex.trim())) {
    c.set(hex.startsWith('#') ? hex : `#${hex}`);
  }
  return c;
}

/**
 * Crea un elemento <video> listo para textura (muted + playsInline para autoplay post-gesto).
 * @param {string} url
 * @param {{ loop?: boolean, muted?: boolean }} [opts]
 */
export function createVideoElement(url, opts = {}) {
  const video = document.createElement('video');
  video.src = url;
  video.crossOrigin = 'anonymous';
  video.loop = opts.loop !== false;
  video.muted = opts.muted !== false;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.preload = 'metadata';
  return video;
}

/**
 * Material según modo. `alpha` = VideoTexture + transparent.
 * `chroma` = shader básico (umbral fijo; ajustar en producción).
 *
 * @param {HTMLVideoElement} video
 * @param {{ mode?: 'alpha' | 'chroma', chromaKey?: string, similarity?: number }} [opts]
 */
export function createTransparentVideoMaterial(video, opts = {}) {
  const mode = opts.mode === 'chroma' ? 'chroma' : 'alpha';
  const texture = new THREE.VideoTexture(video);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  if (mode === 'alpha') {
    return {
      mode,
      texture,
      material: new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    };
  }

  const key = parseChromaKey(opts.chromaKey);
  const similarity = Number.isFinite(opts.similarity) ? opts.similarity : 0.35;
  const material = new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture },
      keyColor: { value: key },
      similarity: { value: similarity },
    },
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D map;
      uniform vec3 keyColor;
      uniform float similarity;
      varying vec2 vUv;
      void main() {
        vec4 tex = texture2D(map, vUv);
        float diff = distance(tex.rgb, keyColor);
        float alpha = smoothstep(similarity, similarity + 0.15, diff);
        gl_FragColor = vec4(tex.rgb, alpha);
      }
    `,
  });

  return { mode, texture, material };
}

/**
 * Monta un plano de video bajo `parent` si la URL responde.
 * Devuelve controlador o `null` si el asset no está (stub sin fingir contenido).
 *
 * @param {THREE.Object3D} parent
 * @param {{
 *   url: string,
 *   mode?: 'alpha' | 'chroma',
 *   chromaKey?: string,
 *   width?: number,
 *   height?: number,
 *   position?: [number, number, number],
 * }} opts
 */
export async function attachTransparentVideo(parent, opts) {
  if (!opts?.url) return null;

  const probe = await fetch(opts.url, { method: 'GET', cache: 'no-store' }).catch(() => null);
  if (!probe || !probe.ok) {
    console.info('[transparent-video] asset ausente (stub no-op):', opts.url);
    return null;
  }
  const ct = probe.headers.get('content-type') || '';
  if (ct.includes('text/html')) {
    console.info('[transparent-video] URL devolvió HTML — no montar:', opts.url);
    return null;
  }
  // Liberar cuerpo; el <video> volverá a pedir la URL.
  await probe.arrayBuffer().catch(() => {});

  const video = createVideoElement(opts.url);
  const { material, texture, mode } = createTransparentVideoMaterial(video, opts);
  const w = opts.width ?? 0.9;
  const h = opts.height ?? 1.2;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), material);
  mesh.name = 'transparent-video-plane';
  const [x, y, z] = opts.position || [0.55, 0.7, 0];
  mesh.position.set(x, y, z);
  parent.add(mesh);

  let started = false;
  async function play() {
    try {
      await video.play();
      started = true;
    } catch (err) {
      console.warn('[transparent-video] play bloqueado (gesto/permiso):', err?.message || err);
    }
  }

  function update() {
    if (started && video.readyState >= 2) texture.needsUpdate = true;
  }

  function dispose() {
    parent.remove(mesh);
    mesh.geometry.dispose();
    material.dispose();
    texture.dispose();
    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  return { mesh, video, mode, play, update, dispose };
}

/**
 * Lee query params de herramienta: video, videoMode, chromaKey.
 * @param {URLSearchParams} [params]
 */
export function readVideoHookParams(params = new URLSearchParams(location.search)) {
  const url = params.get('video');
  if (!url) return null;
  const mode = params.get('videoMode') === 'chroma' ? 'chroma' : 'alpha';
  const chromaKey = params.get('chromaKey') || '#00ff00';
  return { url, mode, chromaKey };
}
